'use client'

import Image from 'next/image'
import Link from 'next/link'
import Container from '@/components/ui/Container'
import { NewsFavicon } from '@/components/NewsFavicon'
import SignUpForm from '@/components/forms/SignUpForm'
import { TESTIMONIALS } from '@/constants/testimonials'
import { TestimonialsSlider } from '@/components/testimonials-slider'
import { todaysDate } from '@/utils/date'

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL

// Outlets to exclude from image selection
const EXCLUDED_IMAGE_OUTLETS = ['bbc-news', 'abc-news']

// Helper to check if outlet is valid for image selection
function isValidImageOutlet(outlet) {
  if (!outlet) return false
  if (EXCLUDED_IMAGE_OUTLETS.includes(outlet.slug)) return false
  if (outlet.bias > 20 || outlet.bias < -20) return false
  if (outlet.reliability < 24) return false
  return true
}

// Helper to get first valid image (skipping excluded outlets)
function getStoryImage(articles) {
  if (!articles || articles.length === 0) return null
  const validArticle = articles.find(
    (article) => article.image && isValidImageOutlet(article.outlet)
  )
  return validArticle?.image || null
}

// Helper to generate story URL
function getStoryUrl(story) {
  if (!story.published_date || !story.slug || !story.category) return '#'
  const date = new Date(story.published_date)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `/news/${story.category.toLowerCase()}/${year}/${month}/${day}/${story.slug}`
}

// Helper to get simple bias label
function getBiasLabel(articles) {
  if (!articles || articles.length === 0) return null

  let left = 0, right = 0
  articles.forEach(article => {
    const bias = article.outlet?.bias ?? 0
    if (bias < -6) left++
    else if (bias > 6) right++
  })

  const total = articles.length
  const leftPct = Math.round((left / total) * 100)
  const rightPct = Math.round((right / total) * 100)

  if (leftPct >= 50) {
    return { text: `${leftPct}% Left`, color: 'text-blue-600' }
  } else if (rightPct >= 50) {
    return { text: `${rightPct}% Right`, color: 'text-red-600' }
  } else {
    return { text: 'Balanced', color: 'text-muted-foreground' }
  }
}

// Format category name
function formatCategory(category) {
  if (!category) return ''
  return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase()
}

// Featured Story Card (Top 3 - equal weight)
function FeaturedStoryCard({ story, rank }) {
  const storyUrl = getStoryUrl(story)
  const firstImage = getStoryImage(story.articles)
  const biasLabel = getBiasLabel(story.articles)

  // Get unique outlets from first 3 articles for favicons
  const recentOutlets = story.articles
    ?.slice(0, 3)
    .map(a => a.outlet)
    .filter(Boolean)
    .filter((outlet, index, self) =>
      index === self.findIndex(o => o?.slug === outlet?.slug)
    ) || []

  return (
    <article className="group relative">
      {firstImage && (
        <div className="relative aspect-[4/3] mb-4 overflow-hidden">
          <Image
            src={firstImage}
            alt={story.title}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            sizes="(max-width: 768px) 100vw, 33vw"
            priority={rank <= 2}
          />
          <div className="absolute top-3 left-3 w-8 h-8 bg-foreground text-background flex items-center justify-center font-sans text-sm font-bold">
            {rank}
          </div>
        </div>
      )}
      <div className="space-y-2">
        <h2 className="font-serif text-xl md:text-2xl font-bold leading-tight tracking-tight">
          <Link
            href={storyUrl}
            className="hover:text-foreground/70 transition-colors after:absolute after:inset-0"
          >
            {story.title}
          </Link>
        </h2>
        <div className="byline flex items-center gap-2">
          {recentOutlets.length > 0 && (
            <div className="flex -space-x-1">
              {recentOutlets.map((outlet) => (
                <NewsFavicon
                  key={outlet.slug}
                  src={`${SUPABASE_URL}/storage/v1/object/public/public_assets/news-favicons/${outlet.slug}.jpg`}
                  alt={outlet.name || outlet.slug}
                  faviconSize="xs"
                  className="ring-1 ring-background"
                />
              ))}
            </div>
          )}
          <span>{story.articles?.length || 0} sources</span>
          {biasLabel && (
            <>
              <span className="text-border">•</span>
              <span className={biasLabel.color}>{biasLabel.text}</span>
            </>
          )}
          {story.category && (
            <>
              <span className="text-border">•</span>
              <span>{formatCategory(story.category)}</span>
            </>
          )}
        </div>
      </div>
    </article>
  )
}

// Compact Story Card (Stories 4-10) - Number + content
function CompactStoryCard({ story, rank }) {
  const storyUrl = getStoryUrl(story)
  const biasLabel = getBiasLabel(story.articles)

  return (
    <article className="group relative flex items-start gap-3">
      <span className="flex-shrink-0 font-serif text-2xl font-bold text-muted-foreground/40 w-6">
        {rank}
      </span>
      <div className="flex-1 min-w-0">
        <h3 className="font-serif text-base font-bold leading-snug tracking-tight line-clamp-2 mb-1">
          <Link
            href={storyUrl}
            className="hover:text-foreground/70 transition-colors after:absolute after:inset-0"
          >
            {story.short_title || story.title}
          </Link>
        </h3>
        <div className="byline flex items-center gap-2 flex-wrap">
          <span>{story.articles?.length || 0} sources</span>
          {biasLabel && (
            <>
              <span className="text-border">•</span>
              <span className={biasLabel.color}>{biasLabel.text}</span>
            </>
          )}
          {story.category && (
            <>
              <span className="text-border">•</span>
              <span>{formatCategory(story.category)}</span>
            </>
          )}
        </div>
      </div>
    </article>
  )
}

// Featured Bias Story (first story in left/right sections)
function FeaturedBiasStory({ story, accentColor }) {
  const storyUrl = getStoryUrl(story)
  const firstImage = getStoryImage(story.articles)
  const biasLabel = getBiasLabel(story.articles)

  return (
    <article className="group relative mb-4 pb-4 border-b border-current/10">
      {firstImage && (
        <div className="relative aspect-[16/9] mb-3 overflow-hidden">
          <Image
            src={firstImage}
            alt={story.short_title || story.title}
            fill
            className="object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
      )}
      <h3 className="font-serif text-base font-bold leading-snug tracking-tight mb-2">
        <Link
          href={storyUrl}
          className="hover:text-foreground/70 transition-colors after:absolute after:inset-0"
        >
          {story.short_title || story.title}
        </Link>
      </h3>
      <div className="byline flex items-center gap-2">
        <span>{story.articles?.length || 0} sources</span>
        {biasLabel && biasLabel.text !== 'Balanced' && (
          <>
            <span className="text-border">•</span>
            <span className={biasLabel.color}>{biasLabel.text}</span>
          </>
        )}
      </div>
    </article>
  )
}

// Compact Bias Story (remaining stories in left/right sections)
function CompactBiasStory({ story, showBias = false }) {
  const storyUrl = getStoryUrl(story)
  const biasLabel = getBiasLabel(story.articles)

  return (
    <article className="group relative py-3">
      <h3 className="font-serif text-sm font-bold leading-snug tracking-tight line-clamp-2 mb-1">
        <Link
          href={storyUrl}
          className="hover:text-foreground/70 transition-colors after:absolute after:inset-0"
        >
          {story.short_title || story.title}
        </Link>
      </h3>
      <div className="byline flex items-center gap-2">
        <span>{story.articles?.length || 0} sources</span>
        {biasLabel && biasLabel.text !== 'Balanced' && (
          <>
            <span className="text-border">•</span>
            <span className={biasLabel.color}>{biasLabel.text}</span>
          </>
        )}
      </div>
    </article>
  )
}

export default function NewsletterLayout({ topStories, leftStories, rightStories }) {
  const featuredStories = topStories.slice(0, 3)
  const moreStories = topStories.slice(3, 10)

  return (
    <section className="bg-background">
      <Container className="section-padding">
        {/* Masthead */}
        <div className="text-center mb-10 md:mb-12">
          <div className="font-sans text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
            Daily Pano-rama
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif tracking-tight mb-3">
            {todaysDate()}
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto mb-4">
            Today's biggest stories ranked by how many outlets are covering them — from{' '}
            <span className="text-blue-600 font-medium">Left</span>,{' '}
            <span className="text-muted-foreground font-medium">Center</span>, and{' '}
            <span className="text-red-600 font-medium">Right</span>.
          </p>
          <Link
            href="/how-it-works"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors font-sans"
          >
            How it works →
          </Link>
        </div>

        {/* Featured Stories (Top 3) - Equal weight */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 pb-12 border-b border-border">
          {featuredStories.map((story, index) => (
            <FeaturedStoryCard
              key={story.slug || index}
              story={story}
              rank={index + 1}
            />
          ))}
        </div>

        {/* More Stories (4-10) - 3 Column Grid */}
        {moreStories.length > 0 && (
          <div className="mb-12 pb-12 border-b border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {moreStories.map((story, index) => (
                <CompactStoryCard
                  key={story.slug || index}
                  story={story}
                  rank={index + 4}
                />
              ))}
            </div>
          </div>
        )}

        {/* From the Left / From the Right - 2 Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* From the Left */}
          <div className="bg-blue-600/5 border-t-4 border-blue-600 p-6 md:p-8">
            <div className="mb-6">
              <h2 className="font-serif text-2xl font-bold text-blue-600 mb-1">
                From the Left
              </h2>
              <p className="text-sm text-muted-foreground">
                Stories covered mostly by left-leaning outlets
              </p>
            </div>
            {leftStories.length > 0 ? (
              <>
                <FeaturedBiasStory
                  story={leftStories[0]}
                  accentColor="bg-blue-600"
                />
                <div className="divide-y divide-blue-600/20">
                  {leftStories.slice(1, 5).map((story, index) => (
                    <CompactBiasStory key={story.slug || index} story={story} />
                  ))}
                </div>
              </>
            ) : (
              <p className="text-muted-foreground py-4 text-sm">No stories available</p>
            )}
          </div>

          {/* From the Right */}
          <div className="bg-red-600/5 border-t-4 border-red-600 p-6 md:p-8">
            <div className="mb-6">
              <h2 className="font-serif text-2xl font-bold text-red-600 mb-1">
                From the Right
              </h2>
              <p className="text-sm text-muted-foreground">
                Stories covered mostly by right-leaning outlets
              </p>
            </div>
            {rightStories.length > 0 ? (
              <>
                <FeaturedBiasStory
                  story={rightStories[0]}
                  accentColor="bg-red-600"
                />
                <div className="divide-y divide-red-600/20">
                  {rightStories.slice(1, 5).map((story, index) => (
                    <CompactBiasStory key={story.slug || index} story={story} />
                  ))}
                </div>
              </>
            ) : (
              <p className="text-muted-foreground py-4 text-sm">No stories available</p>
            )}
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="text-center mb-6">
            <h2 className="font-sans text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
              What Readers Are Saying
            </h2>
            <p className="font-serif text-2xl md:text-3xl font-bold">
              Join 10,000+ informed readers
            </p>
          </div>

          <TestimonialsSlider testimonials={TESTIMONIALS} />
        </div>

        {/* Final CTA Section */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="max-w-xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-3">
              Get the Full Picture
            </h2>
            <p className="text-muted-foreground mb-6">
              Join thousands of readers who start their day with a balanced view of the news. Free daily newsletter, no spam.
            </p>
            <div className="max-w-sm mx-auto">
              <SignUpForm
                buttonLabel="Get 5 Free Issues"
                trackingLocation="homepage_bottom_cta"
              />
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
